/**
 * Vue Router
 *
 * @package Muval Frontend Project
 * @copyright 2021 Muval Pty Ltd. All rights reserved.
 * @author Tim Maier
 */

// Domains
import orderRoutes from './domains/orderRoutes';
import depotRoutes from './domains/depotRoutes';
import couponRoutes from './domains/couponRoutes';
import staffRoutes from './domains/staffRoutes';
import leadRoutes from './domains/leadRoutes';
import companyRoutes from './domains/companyRoutes';
import localPriceRoutes from './domains/localPriceRoutes';
import interstateRoutes from './domains/interstateRoutes';
import fleetRoutes from './domains/fleetRoutes';
// import companiesRoutes from './domains/companiesRoutes';
import tenantsRoutes from './domains/tenantsRoutes';
import usersRoutes from './domains/usersRoutes';
import communicationRoutes from './domains/communicationRoutes';
import jobRoutes from './domains/jobRoutes';
import affiliateRoutes from './domains/affiliateRoutes';
import providerRoutes from './domains/providerRoutes';
import settingsRoutes from './domains/settingsRoutes';
import ancillaryRoutes from './domains/ancillaryRoutes';
import quoteRoutes from './domains/quoteRoutes';
import newsRoutes from './domains/newsRoutes';
import toolsRoutes from './domains/toolsRoutes';
import schedulerRoutes from './domains/schedulerRoutes';
import routesRoutes from './domains/routesRoutes';
import formRoutes from './domains/formRoutes';
import siteInspectionSchedulerRoutes from './domains/siteInspectionSchedulerRoutes';
import { useUserStore } from '@store/user';
import { useAppStore } from '../store/app';

const router = createRouter({
    scrollBehavior(to, from, savedPosition) {
        if (to.hash) {
            return {
                el: to.hash,
            };
        }
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0 };
        }
    },
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            redirect: { name: 'Dashboard' },
        },
        {
            path: '/login',
            name: 'Login',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                hideForAuth: true,
                fullscreen: true,
            },
            components: {
                default: () => import('../views/SignInView.vue'),
            },
        },
        {
            path: '/reset-password',
            name: 'ForgotPassword',
            props: {
                default: (route) => ({ token: route.query.token }),
            },
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                hideForAuth: true,
                fullscreen: true,
            },
            components: {
                default: () => import('../views/ForgotPassword.vue'),
            },
        },
        /*{
            path: '/register',
            name: 'Register',
            meta: {
                requiresAuth: false,
                allowRoles: null,
                denyRoles: null,
                hideForAuth: true,
                fullscreen: true,
            },
            components: {
                default: () => import('../views/RegistrationView.vue'),
            },
        },*/
        {
            path: '/dashboard',
            name: 'Dashboard',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('../views/dashboard/DashboardIndex.vue'),
            },
        },
        {
            path: '/me',
            name: 'My Account',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('../views/MyAccountView.vue'),
            },
        },
        {
            path: '/search',
            name: 'Search',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('../views/search/GlobalSearch.vue'),
            },
        },
        {
            path: '/development',
            name: 'Development',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('../views/DevPage.vue'),
            },
        },
        {
            path: '/help',
            name: 'Help',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                title: 'Need help?',
            },
            components: {
                default: () => import('@views/HelpPage.vue'),
            },
        },

        // Domain Routes
        ...leadRoutes,
        ...orderRoutes,
        ...depotRoutes,
        ...couponRoutes,
        ...tenantsRoutes,
        ...usersRoutes,
        ...companyRoutes,
        // ...companiesRoutes,
        ...staffRoutes,
        ...communicationRoutes,
        ...localPriceRoutes,
        ...interstateRoutes,
        ...fleetRoutes,
        ...jobRoutes,
        ...affiliateRoutes,
        ...providerRoutes,
        ...settingsRoutes,
        ...ancillaryRoutes,
        ...quoteRoutes,
        ...newsRoutes,
        ...toolsRoutes,
        ...schedulerRoutes,
        ...routesRoutes,
        ...formRoutes,
        ...siteInspectionSchedulerRoutes,
        {
            path: '/inbox/calls',
            name: 'InboxCalls',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('../views/inbox/CallsIndex.vue'),
            },
        },
        {
            path: '/inbox/sms',
            name: 'InboxSms',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('../views/inbox/SmsIndex.vue'),
            },
        },
        {
            path: '/finance',
            name: 'Finance',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
                title: 'Finance',
            },
            components: {
                default: () => import('../views/finance/FinanceIndex.vue'),
            },
        },
        {
            path: '/payouts',
            name: 'Payouts',
            meta: {
                requiresAuth: true,
                allowRoles: ['SUPER', 'OWNER', 'FINANCE'],
                denyRoles: null,
                title: 'Payouts',
            },
            components: {
                default: () => import('../views/payouts/PayoutsIndex.vue'),
            },
        },
        {
            path: '/communications',
            name: 'Communications',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('../views/communication/CommunicationsIndex.vue'),
            },
        },
        {
            path: '/not_authorised',
            name: '403Page',
            meta: {
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('@views/Error403Page.vue'),
            },
        },
        {
            path: '/service_unavailable',
            name: '503Page',
            meta: {
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('@views/Error503Page.vue'),
            },
        },
        {
            path: '/not_found',
            name: '404Page',
            meta: {
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('@views/Error404Page.vue'),
            },
        },
        {
            path: '/order-not-found',
            name: 'OrderNotFound',
            meta: {
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('@views/OrderNotFound.vue'),
            },
        },
        {
            path: '/:pathMatch(.*)*',
            meta: {
                requiresAuth: true,
                allowRoles: null,
                denyRoles: null,
            },
            components: {
                default: () => import('@views/Error404Page.vue'),
            },
        },
    ],
});
/*eslint-disable */

router.beforeEach((to, from, next) => {
    const app = useAppStore();

    // Find abort controller and abort all requests tied to it and then remove it from the store
    const foundAbortController = app.axiosAbortControllerList[from.path];

    if (foundAbortController && from.path != '/' && from.path != to.path) {
        // console.log('foundAbortController', foundAbortController);
        foundAbortController.controller.abort();
        delete app.axiosAbortControllerList[from.path];
    }

    app.side = false;

    const currentUser = useUserStore();
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    const hideForAuth = to.matched.some((record) => record.meta.hideForAuth);
    const isCentralRoute = to.matched.some((record) => record.meta.isCentralRoute);

    const deepestRecord = to.matched[to.matched.length - 1] ?? {};
    const allowRoles = deepestRecord.meta?.allowRoles ?? null;
    const denyRoles = deepestRecord.meta?.denyRoles ?? null;
    if (requiresAuth && !currentUser.isAuthenticated) {
        const loginPath = window.location.pathname;

        next({ name: 'Login', query: { from: loginPath } });
    } else if (requiresAuth && currentUser.isAuthenticated && isCentralRoute && !currentUser.isCentralContext) {
        next({ name: 'Dashboard' });
    } else if (hideForAuth && currentUser.isAuthenticated) {
        next({ name: 'Dashboard' });
    } else {
        // if roles are not defined, allow access
        if (allowRoles === null && denyRoles == null) {
            next();
            return;
        }

        // if deny roles are defined, check if user has the role and deny
        if (denyRoles && denyRoles.length > 0 && currentUser.hasAnyRole(denyRoles, false)) {
            // Prevent access if user has the role denied
            next({ name: '403Page' });
            return;
        }

        // if allow roles are defined, check if user has the role
        if (allowRoles && allowRoles.length > 0) {
            // Admin roles have access to everything
            if (currentUser.hasAnyRole(['OWNER', 'ADMIN'])) {
                next();
                return;
            }
            if (currentUser.hasAnyRole(allowRoles)) {
                // Allow access if user has the role allowed
                next();
                return;
            } else {
                // Prevent access if user does not have the role allowed
                next({ name: '403Page' });
                return;
            }
        }

        // If no roles are matched, allow access
        next();
        return;
    }
});

export default router;
